import type { NostrEvent, NostrFilter } from '@nostrify/nostrify';

/**
 * Query Nostr relays with the browser WebSocket API — no NPool, no batcher,
 * no first-EOSE race. Waits until each relay EOSES or times out, then merges.
 *
 * Used for replaceable user lists (NIP-65, BUD-03) that often live on a
 * subset of relays and get dropped by fast-path pool queries.
 */
export async function rawRelayQuery(
  relayUrls: string[],
  filters: NostrFilter[],
  opts?: { timeoutMs?: number; signal?: AbortSignal },
): Promise<NostrEvent[]> {
  const timeoutMs = opts?.timeoutMs ?? 10_000;
  const signal = opts?.signal;
  const urls = [
    ...new Set(
      relayUrls
        .map((u) => u.trim().replace(/\/+$/, ''))
        .filter((u) => /^wss?:\/\//i.test(u)),
    ),
  ].slice(0, 20);

  if (urls.length === 0 || filters.length === 0) return [];
  if (signal?.aborted) return [];

  const results = await Promise.all(
    urls.map((url) => queryOneRelay(url, filters, timeoutMs, signal)),
  );

  // Dedupe by event id, keep newest created_at if dupes differ (shouldn't)
  const byId = new Map<string, NostrEvent>();
  for (const batch of results) {
    for (const e of batch) {
      if (!e?.id || !e.sig) continue;
      const prev = byId.get(e.id);
      if (!prev || e.created_at > prev.created_at) byId.set(e.id, e);
    }
  }
  return [...byId.values()];
}

function queryOneRelay(
  url: string,
  filters: NostrFilter[],
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<NostrEvent[]> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve([]);
      return;
    }

    const events: NostrEvent[] = [];
    let settled = false;
    let ws: WebSocket | null = null;
    const subId = `od${Math.random().toString(36).slice(2, 10)}`;

    const finish = () => {
      if (settled) return;
      settled = true;
      try {
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(['CLOSE', subId]));
        }
      } catch {
        /* ignore */
      }
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      signal?.removeEventListener('abort', onAbort);
      clearTimeout(timer);
      resolve(events);
    };

    const onAbort = () => finish();
    signal?.addEventListener('abort', onAbort, { once: true });

    const timer = setTimeout(finish, timeoutMs);

    try {
      ws = new WebSocket(url);
    } catch {
      finish();
      return;
    }

    ws.onopen = () => {
      try {
        ws?.send(JSON.stringify(['REQ', subId, ...filters]));
      } catch {
        finish();
      }
    };

    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(String(ev.data)) as unknown[];
        if (!Array.isArray(msg) || msg.length < 2) return;
        if (msg[0] === 'EVENT' && msg[1] === subId && msg[2]) {
          events.push(msg[2] as NostrEvent);
        } else if (msg[0] === 'EOSE' && msg[1] === subId) {
          finish();
        } else if (msg[0] === 'CLOSED' && msg[1] === subId) {
          finish();
        }
      } catch {
        /* ignore malformed frames */
      }
    };

    ws.onerror = () => finish();
    ws.onclose = () => finish();
  });
}

/** Newest event of a given kind from a raw query result. */
export function newestOfKind(
  events: NostrEvent[],
  kind: number,
): NostrEvent | undefined {
  let best: NostrEvent | undefined;
  for (const e of events) {
    if (e.kind !== kind) continue;
    if (!best || e.created_at > best.created_at) best = e;
  }
  return best;
}
